/**
 * ── NO HAND-TYPED LINE BREAKS IN BLOCK COPY (2026-10-30, Layne Armenta, Teams) ──────────
 * "We'd like to stop authors from pressing Enter or Shift+Enter in any field, so copy
 * can't be broken onto new lines by hand."
 *
 * Every copy field on a Content Builder (ECH) block drops line breaks: Enter and
 * Shift+Enter do nothing, and a break that arrives by paste becomes a space. The box still
 * wraps long copy so it stays readable while typing. A block field can opt back in with
 * `"allowBreaks": true` in its guardrails, for the day one genuinely needs separate lines.
 *
 * Why here and not as a block `pattern`: patterns only WARN (echPattern.ts), so a break
 * the author ignored the warning on would still ship — as a <br> (core.escML).
 */
import { echMeta } from "./echRegistry";

/** every kind of line break, replaced with one space */
export function stripBreaks(s: string): string {
  return String(s ?? "").replace(/\r\n|\r|\n/g, " ");
}

/** true only when the block's own guardrails let this field keep line breaks */
export function echBreaksAllowed(type: string, k: string): boolean {
  const m = echMeta(String(type || ""));
  const rule = m ? ((m.declared || {}) as Record<string, { allowBreaks?: unknown }>)[String(k || "")] : undefined;
  return !!(rule && rule.allowBreaks === true);
}

/* ── THE BULLET EXCEPTION (2026-10-30, Layne + Justin, Teams 3:12 / 3:32 PM) ─────────────
   "They shouldn't be allowed to hit enter or shift+enter in any of those boxes EXCEPT if they
   have bullet points." Only the • character counts as a bullet (Justin: "only a bullet").

   Body copy (paragraphs, never a headline) may keep a line break when the line AFTER it
   starts with •. That allows a lead-in line followed by bullets, and bullets one per line.
   Every other break becomes a space, so a break typed or pasted before ordinary text is
   still dropped. In the email each bullet line renders on its own line (core.escML → <br>). */
export const BULLET = "\u2022";

const isBulletLine = (line: string): boolean => line.replace(/^\s+/, "").startsWith(BULLET);

/** keep only the line breaks that start a bullet line; every other break becomes a space */
export function keepBulletBreaks(s: string): string {
  const lines = String(s ?? "").split(/\r\n|\r|\n/);
  let out = lines[0] ?? "";
  for (let i = 1; i < lines.length; i++) {
    out = isBulletLine(lines[i]) ? out.replace(/[ \t]+$/, "") + "\n" + lines[i] : out + " " + lines[i];
  }
  return out;
}

/**
 * Enter at `caret` in a bullets-allowed box. On a line that starts with • (and has text after
 * it) a new "• " line opens below and the caret lands after it. Anywhere else, or on an empty
 * bullet, Enter does nothing (null).
 */
export function bulletEnter(value: string, caret: number): { value: string; caret: number } | null {
  const v = String(value ?? "");
  const at = Math.max(0, Math.min(v.length, caret));
  const start = v.lastIndexOf("\n", at - 1) + 1;
  const line = v.slice(start, at);
  if (!isBulletLine(line)) return null;
  if (!line.replace(/^\s+/, "").slice(1).trim()) return null;   /* an empty bullet: nothing to continue */
  const ins = "\n" + BULLET + " ";
  return { value: v.slice(0, at) + ins + v.slice(at), caret: at + ins.length };
}

/**
 * Does this ECH field take the bullet exception? Body copy only: a paragraph field (p1…p4) or a
 * field named or labelled as body copy / a paragraph — never a headline or title. A block can
 * also say so itself with `"bullets": true` (or turn it off with `false`).
 */
export function echBulletsAllowed(type: string, k: string): boolean {
  const m = echMeta(String(type || ""));
  if (!m) return false;
  const rule = ((m.declared || {}) as Record<string, { bullets?: unknown; label?: unknown }>)[String(k || "")];
  if (rule && typeof rule.bullets === "boolean") return rule.bullets;
  const key = String(k || "");
  const label = String((rule && rule.label) || "");
  if (/head|title/i.test(key) || /head|title/i.test(label)) return false;
  return /^p\d+$/i.test(key) || /body|paragraph/i.test(key) || /body|paragraph/i.test(label);
}
